import type { GridColDef } from '@mui/x-data-grid-pro';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Edit, OpenInNew, Sync } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApi } from '@shared/services/api';

interface PlanFeatures {
  [key: string]: unknown;
}

interface AdminAccountRow {
  id: number;
  name: string;
  createdAt: string;
  ownerEmail: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscription: null | {
    id: number;
    planSlug: string;
    planName: string | null;
    status: string;
    term: string;
    startDate: string;
    endDate: string | null;
    featureOverrides: PlanFeatures | null;
    planFeatures: PlanFeatures;
    effectiveFeatures: PlanFeatures;
  };
}

function formatDate(date: string | null | undefined): string {
  return date ? dayjs(date).format('YYYY-MM-DD') : '-';
}

function stripeCustomerUrl(stripeCustomerId: string | null): string | null {
  if (!stripeCustomerId) return null;
  return `https://dashboard.stripe.com/customers/${stripeCustomerId}`;
}

function AdminAccountTable(): React.ReactElement {
  const api = useApi();
  const [rows, setRows] = useState<AdminAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingAccount, setEditingAccount] = useState<AdminAccountRow | null>(null);

  const apiRef = useRef(api);
  apiRef.current = api;

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchAccounts = useCallback(async (query: string) => {
    setLoading(true);
    const path = query ? `/admin/account?q=${encodeURIComponent(query)}` : '/admin/account';
    const result: any = await apiRef.current.get(path);
    if (result?.error) {
      setError(result.message ?? 'Failed to load accounts');
    } else {
      setRows((result?.data ?? []) as AdminAccountRow[]);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAccounts(debouncedSearch);
  }, [fetchAccounts, debouncedSearch]);

  const handleStripeLinked = useCallback((accountId: number, customerId: string) => {
    setRows((prev) => prev.map((r) => (r.id === accountId ? { ...r, stripeCustomerId: customerId } : r)));
  }, []);

  const columns: GridColDef<AdminAccountRow>[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80, type: 'number' },
      { field: 'name', headerName: 'Account', flex: 1.2, minWidth: 180 },
      {
        field: 'createdAt',
        headerName: 'Created',
        width: 120,
        valueFormatter: (value) => formatDate(value),
      },
      {
        field: 'planSlug',
        headerName: 'Plan',
        width: 140,
        valueGetter: (_v, row) => row.subscription?.planSlug ?? '-',
      },
      {
        field: 'ownerEmail',
        headerName: 'Owner Email',
        flex: 1,
        minWidth: 200,
        valueGetter: (_v, row) => row.ownerEmail ?? '-',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: ({ row }) => {
          const status = row.subscription?.status ?? 'none';
          return (
            <Chip size="small" label={status} color={status === 'active' ? 'success' : 'default'} variant="outlined" />
          );
        },
      },
      {
        field: 'stripe',
        headerName: 'Stripe',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const url = stripeCustomerUrl(row.stripeCustomerId);
          if (!url) {
            return (
              <LinkStripeButton accountId={row.id} onLinked={(customerId) => handleStripeLinked(row.id, customerId)} />
            );
          }
          return (
            <Button
              size="small"
              endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              Open
            </Button>
          );
        },
      },
      {
        field: 'overrides',
        headerName: 'Overrides',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const hasOverrides =
            row.subscription?.featureOverrides && Object.keys(row.subscription.featureOverrides).length > 0;
          return hasOverrides ? (
            <Chip size="small" label="Custom" color="warning" />
          ) : (
            <Typography variant="caption" color="text.disabled">
              —
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton
            size="small"
            disabled={!row.subscription}
            onClick={() => setEditingAccount(row)}
            aria-label="Edit overrides"
          >
            <Edit fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleStripeLinked],
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by name, ID, or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <Button size="small" onClick={() => setSearchInput('')} disabled={searchInput === ''}>
            Clear
          </Button>
        </Stack>
        <SyncStripePlansButton />
      </Stack>

      <DataGridPro
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />

      {loading && <CircularProgress size={20} sx={{ mt: 2 }} />}

      <AdminAccountOverridesDrawer
        account={editingAccount}
        onClose={() => setEditingAccount(null)}
        onSaved={async () => {
          setEditingAccount(null);
          await fetchAccounts(debouncedSearch);
        }}
      />
    </Box>
  );
}

// ── Sync Stripe plans button ───────────────────────────────────────────────

interface SyncResult {
  synced: Array<{ priceId: string; planSlug: string; term: 'monthly' | 'annual' }>;
  skipped: Array<{ priceId: string; reason: string }>;
}

function SyncStripePlansButton(): React.ReactElement {
  const api = useApi();
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSync = async (): Promise<void> => {
    setSyncing(true);
    setError(null);
    setResult(null);
    const response: any = await api.post('/admin/stripe/sync-plans', {});
    setSyncing(false);
    if (response?.error || response?.success === false) {
      setError(response?.message ?? response?.error?.message ?? 'Failed to sync');
      return;
    }
    setResult(response?.data ?? response);
  };

  return (
    <Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
        onClick={handleSync}
        disabled={syncing}
      >
        {syncing ? 'Syncing…' : 'Sync Stripe plans'}
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Alert
          severity={result.skipped.length > 0 ? 'warning' : 'success'}
          sx={{ mt: 1 }}
          onClose={() => setResult(null)}
        >
          <Typography variant="body2" component="div">
            Synced <strong>{result.synced.length}</strong> price(s), skipped <strong>{result.skipped.length}</strong>.
          </Typography>
          {result.synced.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {result.synced.map((s) => (
                <li key={s.priceId}>
                  <code>
                    {s.planSlug}.{s.term}
                  </code>
                  {' → '}
                  <code>{s.priceId}</code>
                </li>
              ))}
            </Box>
          )}
          {result.skipped.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {result.skipped.map((s) => (
                <li key={s.priceId}>
                  <code>{s.priceId}</code>: {s.reason}
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}
    </Box>
  );
}

// ── Link Stripe button (used in table Stripe column) ──────────────────────

function LinkStripeButton({
  accountId,
  onLinked,
}: {
  accountId: number;
  onLinked: (customerId: string) => void;
}): React.ReactElement {
  const api = useApi();
  const [linking, setLinking] = useState(false);

  const handleLink = async (): Promise<void> => {
    setLinking(true);
    const result: any = await api.post(`/admin/account/${accountId}/link-stripe`, {});
    setLinking(false);
    if (result?.data?.customerId) {
      onLinked(result.data.customerId);
    }
  };

  return (
    <Button
      size="small"
      onClick={handleLink}
      disabled={linking}
      startIcon={linking ? <CircularProgress size={12} /> : undefined}
    >
      {linking ? 'Linking…' : 'Link'}
    </Button>
  );
}

interface DrawerProps {
  account: AdminAccountRow | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function AdminAccountOverridesDrawer({ account, onClose, onSaved }: DrawerProps): React.ReactElement {
  const api = useApi();
  const [draft, setDraft] = useState<string>('{}');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (account?.subscription) {
      const overrides = account.subscription.featureOverrides ?? {};
      setDraft(JSON.stringify(overrides, null, 2));
      setError(null);
    }
  }, [account]);

  const handleSave = async (): Promise<void> => {
    if (!account) return;
    let parsed: PlanFeatures | null;
    try {
      parsed = draft.trim() === '' || draft.trim() === 'null' ? null : JSON.parse(draft);
    } catch (err) {
      setError(`Invalid JSON: ${(err as Error).message}`);
      return;
    }
    if (parsed !== null && (typeof parsed !== 'object' || Array.isArray(parsed))) {
      setError('featureOverrides must be a JSON object or null');
      return;
    }
    setSaving(true);
    const result: any = await api.patch(`/admin/account/${account.id}/feature-overrides`, { featureOverrides: parsed });
    setSaving(false);
    if (result?.error) {
      setError(result.message ?? 'Failed to save');
      return;
    }
    await onSaved();
  };

  const handleClear = (): void => {
    setDraft('null');
  };

  const handleSyncStripe = async (): Promise<void> => {
    if (!account) return;
    setSyncing(true);
    setError(null);
    const result: any = await api.get(`/admin/account/${account.id}/sync-stripe`);
    setSyncing(false);
    if (result?.data?.synced) {
      await onSaved();
    } else {
      setError(result?.data?.reason ?? result?.message ?? 'Sync failed');
    }
  };

  return (
    <Drawer anchor="right" open={!!account} onClose={onClose}>
      <Box sx={{ width: 480, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Edit Feature Overrides
        </Typography>
        {account && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {account.name} — plan <strong>{account.subscription?.planSlug ?? 'none'}</strong>
          </Typography>
        )}

        {account?.subscription && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Plan defaults (read-only)
            </Typography>
            <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>
              {JSON.stringify(account.subscription.planFeatures, null, 2)}
            </pre>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          Overrides JSON (object keys win over plan defaults; use <code>null</code> to clear all overrides)
        </Typography>
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          multiline
          minRows={10}
          fullWidth
          sx={{ mt: 1, mb: 2, fontFamily: 'monospace' }}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {account?.stripeCustomerId && (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={14} /> : <Sync />}
              onClick={handleSyncStripe}
              disabled={syncing || saving}
            >
              {syncing ? 'Syncing…' : 'Sync with Stripe'}
            </Button>
          )}
          <Button onClick={handleClear} disabled={saving}>
            Use defaults
          </Button>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default AdminAccountTable;
